package com.evoapps.evoshift.domain.usecase.marketplace

import com.evoapps.evoshift.domain.model.NotificationData
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.NotificationType
import com.evoapps.evoshift.domain.model.Notification
import com.evoapps.evoshift.domain.model.SwapStatus
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.util.AppClock.now

class RejectSwapRequestUseCase(
    private val shiftRepository: ShiftRepository,
    private val userRepository: UserRepository
) {
    /**
     * [managerNote]: SH-164: the manager's optional reason; the employee sees it on the shift and
     * in the notification (push: "Note: …"). Blank: no reason.
     */
    suspend operator fun invoke(shiftId: String, managerNote: String?): Result<Unit> {
        val note = managerNote?.trim()?.takeIf { it.isNotEmpty() }
        return try {
            val shift = shiftRepository.getShift(shiftId).getOrThrow()
            val request = shift.swapRequest ?: throw IllegalStateException("No active request")

            // An open shift has no owner to give it back to: refusing a claimant puts it back on the
            // marketplace. A swap request is closed as REJECTED with the note (the owner dismisses it).
            val isOpenShift = shift.userId == null && request.status == SwapStatus.TAKEN_PENDING
            val updated = if (isOpenShift) {
                request.copy(status = SwapStatus.AVAILABLE, candidateUserId = null, candidateName = null, managerNote = note)
            } else {
                request.copy(status = SwapStatus.REJECTED, managerNote = note)
            }
            shiftRepository.updateSwapRequest(shiftId, updated).getOrThrow()

            val workplaceId = RosterIds.workplaceIdOf(shift.rosterId)
            val nowStr = now().toString()

            // Notify Initiator (an open shift's initiator is the manager deciding right now)
            if (!isOpenShift) userRepository.sendNotification(
                Notification(
                    userId = request.initiatorUserId,
                    title = "Shift Swap Update",
                    message = "Your request for the shift on ${shift.date} was not processed.${note?.let { " Note: $it" }.orEmpty()}",
                    type = NotificationType.SHIFT_CLAIM_REJECTED,
                    createdAt = nowStr,
                    data = NotificationData.forShift(shift, workplaceId, NotificationData.NOTE to note)
                )
            )

            // Notify Candidate if there was one
            request.candidateUserId?.let { candidateId ->
                userRepository.sendNotification(
                    Notification(
                        userId = candidateId,
                        title = "Shift Claim Rejected",
                        message = "Your request to take the shift on ${shift.date} was rejected.${note?.let { " Note: $it" }.orEmpty()}",
                        type = NotificationType.SHIFT_CLAIM_REJECTED,
                        createdAt = nowStr,
                        data = NotificationData.forShift(shift, workplaceId, NotificationData.NOTE to note)
                    )
                )
            }

            Result.success(Unit)
        } catch (e: Exception) {
            Result.failure(e)
        }
    }
}